import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, FC, SetStateAction } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Modal from 'components/Modal';
import ModalContentWrapper from 'components/ModalContentWrapper';

import AssignDevicesSuccess from './AssignDevicesSuccess';
import AddDevicesToMember from './AssignDevicesToMembers';
import ManageDevices from './ManageDevices';

type AssignDevicesToMembers = 'manage' | 'devices' | 'success';

interface AssignDevicesToMembersModalState {
  stage: AssignDevicesToMembers;
  devices: number[];
  newDeviceSerial?: string;
}

export interface AssignDevicesToMembersModalStageProps {
  open: boolean;
  state: AssignDevicesToMembersModalState;
  setState: Dispatch<SetStateAction<AssignDevicesToMembersModalState>>;
  onClose: () => void;
  onCloseProxy: () => void;
  organizationId: number | null;
  userId: number | null;
  hasAssignedDevices: boolean;
  selectedMembers: number[];
}

const StageComponents: Record<
  AssignDevicesToMembers,
  FC<AssignDevicesToMembersModalStageProps>
> = {
  manage: ManageDevices,
  devices: AddDevicesToMember,
  success: AssignDevicesSuccess,
};

interface Props extends DefaultPopupProps {
  organizationId: number | null;
  userId: number | null;
  hasAssignedDevices: boolean;
  selectedMembers: number[];
}

const AssignDevicesToMemberModal: FC<Props> = ({
  onClose,
  open,
  organizationId,
  userId,
  selectedMembers,
  hasAssignedDevices,
}) => {
  const [state, setState] = useState<AssignDevicesToMembersModalState>({
    stage: 'devices',
    devices: [],
  });

  useEffect(() => {
    if (!open) {
      setState(prev => ({ ...prev, email: '', role: '', devices: [] }));
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setState(prev => ({
        ...prev,
        stage: hasAssignedDevices ? 'manage' : 'devices',
      }));
    }
  }, [open, hasAssignedDevices]);

  const StageComponent = useMemo(() => StageComponents[state.stage], [state]);

  const onCloseProxy = useCallback((): void => {
    switch (state.stage) {
      case 'manage':
        setState(prev => ({ ...prev, devices: [] }));
        onClose();
        break;

      case 'devices':
        if (!hasAssignedDevices) {
          setState(prev => ({ ...prev, devices: [] }));
          onClose();
        } else {
          setState(prev => ({ ...prev, devices: [], stage: 'manage' }));
        }
        break;
      case 'success': {
        setState(prev => ({ ...prev }));
      }
    }
  }, [hasAssignedDevices, onClose, state.stage]);

  return (
    <Modal
      open={open}
      onClose={onCloseProxy}
      overrideCloseButtonIcon={
        state.stage !== 'manage' && hasAssignedDevices ? (
          <ArrowBackRoundedIcon />
        ) : undefined
      }
      hideCloseButton={state.stage === 'success'}
    >
      <ModalContentWrapper data-testid="assign-device-to-members-modal">
        <StageComponent
          open={open}
          setState={setState}
          state={state}
          onClose={onClose}
          onCloseProxy={onCloseProxy}
          organizationId={organizationId}
          userId={userId}
          hasAssignedDevices={hasAssignedDevices}
          selectedMembers={selectedMembers}
        />
      </ModalContentWrapper>
    </Modal>
  );
};

export default AssignDevicesToMemberModal;
